import { defineStore } from "pinia";

interface Category {
  id: number;
  attributes: {
    name: string;
    slug: string;
    about: string;
    createdAt: string;
    updatedAt: string;
    publishedAt: string;
  };
}

interface BlogPost {
  id: number;
  attributes: {
    title: string;
    body: string;
    slug: string;
    published: boolean;
    publishedAt: string;
    createdAt: string;
    updatedAt: string;
    excerpt?: string;
    cover?: {
      data?: {
        id: number;
        attributes?: {
          url: string;
          formats?: {
            thumbnail?: { url: string };
            small?: { url: string };
            medium?: { url: string };
          };
        };
      };
    };
    categories?: {
      data: Category[];
    };
    author?: {
      data: {
        id: number;
        attributes: {
          name: string;
          avatar?: {
            data?: {
              attributes?: {
                url: string;
              };
            };
          };
        };
      };
    };
  };
}

interface PaginationInfo {
  page: number;
  pageSize: number;
  pageCount: number;
  total: number;
}

interface BlogResponse {
  data: BlogPost[];
  meta: {
    pagination: PaginationInfo;
  };
}

interface CategoryResponse {
  data: Category[];
  meta: {
    pagination: PaginationInfo;
  };
}

export const useBuzzStore = defineStore({
  id: "buzzStore",

  state: () => ({
    posts: [] as BlogPost[],
    categories: [] as Category[],
    currentPost: null as BlogPost | null,
    pagination: {
      page: 1,
      pageSize: 5,
      pageCount: 0,
      total: 0,
    } as PaginationInfo,
    isLoading: false,
    error: null as string | null,
  }),

  getters: {
    getAllPosts: (state) => state.posts,

    getPostById: (state) => (id: number) => {
      return state.posts.find((post) => post.id === id) || null;
    },

    getPostBySlug: (state) => (slug: string) => {
      return state.posts.find((post) => post.attributes.slug === slug) || null;
    },

    getAllCategories: (state) => state.categories,

    getCategoryBySlug: (state) => (slug: string) => {
      return (
        state.categories.find(
          (category) => category.attributes.slug === slug
        ) || null
      );
    },

    getPostsByCategory: (state) => (categoryId: number) => {
      return state.posts.filter((post) =>
        post.attributes.categories?.data.some((cat) => cat.id === categoryId)
      );
    },

    getPaginationInfo: (state) => state.pagination,

    getFeaturedPosts: (state) => {
      // Return the 5 most recent posts
      return [...state.posts]
        .sort(
          (a, b) =>
            new Date(b.attributes.publishedAt).getTime() -
            new Date(a.attributes.publishedAt).getTime()
        )
        .slice(0, 5);
    },
  },

  actions: {
    // Add the clearError action
    clearError() {
      this.error = null;
    },

    async fetchAllPosts({
      page = 1,
      pageSize = 5,
      sort = "publishedAt:desc",
    } = {}) {
      this.isLoading = true;
      this.error = null; // Reset error before fetching

      try {
        const { find } = useStrapi();
        const response = await find<BlogResponse>("blogs", {
          populate: ["cover", "categories", "author.avatar"],
          pagination: {
            page,
            pageSize,
          },
          sort: [sort],
        });

        this.posts = response.data;
        this.pagination = response.meta.pagination;
        return response.data;
      } catch (error: any) {
        this.error = error.message || "Failed to fetch blog posts";
        console.error("Error fetching blog posts:", error);
        return [];
      } finally {
        this.isLoading = false;
      }
    },

    async fetchPostById(id: number) {
      this.isLoading = true;
      this.error = null; // Reset error before fetching

      try {
        const { findOne } = useStrapi();
        const response = await findOne<{ data: BlogPost }>("blogs", id, {
          populate: ["cover", "categories", "author.avatar"],
        });

        this.currentPost = response.data;
        return response.data;
      } catch (error: any) {
        this.error =
          error.message || `Failed to fetch blog post with ID: ${id}`;
        console.error(`Error fetching blog post with ID ${id}:`, error);
        return null;
      } finally {
        this.isLoading = false;
      }
    },

    async fetchPostBySlug(slug: string) {
      this.isLoading = true;
      this.error = null; // Reset error before fetching

      try {
        const { find } = useStrapi();
        const response = await find<BlogResponse>("blogs", {
          filters: {
            slug: {
              $eq: slug,
            },
          },
          populate: ["cover", "categories", "author.avatar"],
        });

        if (response.data && response.data.length > 0) {
          this.currentPost = response.data[0];
          return response.data[0];
        }
        return null;
      } catch (error: any) {
        this.error =
          error.message || `Failed to fetch blog post with slug: ${slug}`;
        console.error(`Error fetching blog post with slug ${slug}:`, error);
        return null;
      } finally {
        this.isLoading = false;
      }
    },

    async fetchAllCategories() {
      this.isLoading = true;
      this.error = null; // Reset error before fetching

      try {
        const { find } = useStrapi();
        const response = await find<CategoryResponse>("categories", {
          sort: ["name:asc"],
        });

        this.categories = response.data;
        return response.data;
      } catch (error: any) {
        this.error = error.message || "Failed to fetch categories";
        console.error("Error fetching categories:", error);
        return [];
      } finally {
        this.isLoading = false;
      }
    },

    async fetchPostsByCategory(
      categorySlug: string,
      { page = 1, pageSize = 10, sort = "publishedAt:desc" } = {} // Added sort parameter
    ) {
      this.isLoading = true;
      this.error = null; // Reset error before fetching

      try {
        const { find } = useStrapi();
        const response = await find<BlogResponse>("blogs", {
          filters: {
            categories: {
              slug: {
                $eq: categorySlug,
              },
            },
          },
          populate: ["cover", "categories", "author.avatar"],
          pagination: {
            page,
            pageSize,
          },
          sort: [sort], // Use the sort parameter
        });

        this.posts = response.data;
        this.pagination = response.meta.pagination;
        return response.data;
      } catch (error: any) {
        this.error =
          error.message ||
          `Failed to fetch posts for category: ${categorySlug}`;
        console.error(
          `Error fetching posts for category ${categorySlug}:`,
          error
        );
        return [];
      } finally {
        this.isLoading = false;
      }
    },

    async searchPosts(query: string, { page = 1, pageSize = 10, sort = "publishedAt:desc" } = {}) { // Added sort parameter
      this.isLoading = true;
      this.error = null; // Reset error before fetching

      try {
        const { find } = useStrapi();
        const response = await find<BlogResponse>("blogs", {
          filters: {
            $or: [
              {
                title: {
                  $containsi: query,
                },
              },
              {
                body: {
                  $containsi: query,
                },
              },
              {
                excerpt: {
                  $containsi: query,
                },
              },
            ],
          },
          populate: ["cover", "categories", "author.avatar"],
          pagination: {
            page,
            pageSize,
          },
          sort: [sort], // Use the sort parameter
        });

        this.posts = response.data;
        this.pagination = response.meta.pagination;
        return response.data;
      } catch (error: any) {
        this.error =
          error.message || `Failed to search posts with query: ${query}`;
        console.error(`Error searching posts with query ${query}:`, error);
        return [];
      } finally {
        this.isLoading = false;
      }
    },
  },
});
